import { todayInput } from "@/lib/date";

export const KIND_LABEL = { rehearsal: "Rehearsal Notes", show: "Show Notes" };
export const KIND_CHIP = { rehearsal: "border-sky-200 bg-sky-50 text-sky-700", show: "border-rose-200 bg-rose-50 text-rose-700" };
export const DEPTS = [
  ["producers", "Producers"], ["front_of_house", "Front of House"], ["scenic", "Scenic"], ["props", "Props"],
  ["lighting", "Lighting"], ["sound", "Sound"], ["costume", "Costume"], ["makeup", "Makeup"], ["general_comments", "General Comments"],
];
export const TIME_ROWS = [["act1", "Act I"], ["intermission", "Intermission"], ["act2", "Act 2"]];
export const STATUS = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600" },
  pending: { label: "Pending review", cls: "bg-amber-100 text-amber-700" },
  returned: { label: "Returned", cls: "bg-red-100 text-red-700" },
  published: { label: "Published", cls: "bg-emerald-100 text-emerald-700" },
};

const blankTimes = () => ({ act1: { time: "", duration: "" }, intermission: { time: "", duration: "" }, act2: { time: "", duration: "" }, total: { time: "" } });

let seq = 0;
export const uid = () => `r${Date.now().toString(36)}${(seq++).toString(36)}`;
export const blankScene = () => ({ key: uid(), scene: "", time: "" });

// Scene Run MM:SS helpers (seconds stored; digits fill from the right, so "732" → 7:32).
export const MAX_SCENE_SEC = 5999;
const pad = (n) => String(n).padStart(2, "0");
export const fmtClock = (sec) => {
  if (sec == null) return "—";
  const s = Math.round(sec), h = Math.floor(s / 3600);
  return h ? `${h}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}` : `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
};
export const maskClock = (v) => {
  const d = v.replace(/\D/g, "").replace(/^0+/, "").slice(0, 4);
  return d.length > 2 ? `${d.slice(0, -2)}:${d.slice(-2)}` : d;
};
export const parseClock = (v) => {
  const d = (v || "").replace(/\D/g, "");
  return d ? Math.min(MAX_SCENE_SEC, Number(d.slice(0, -2) || 0) * 60 + Number(d.slice(-2))) : null;
};
export const sumSeconds = (vals) => vals.reduce((t, v) => t + (v || 0), 0);
export const sceneKey = (name) => (name || "").trim().replace(/\s+/g, " ").toLowerCase();
// Stopwatch: { key, start (ms epoch), base (seconds already on the row) } — wall-clock based so screen locks don't drift.
export const liveSeconds = (t) => Math.min(MAX_SCENE_SEC, t.base + Math.floor((Date.now() - t.start) / 1000));

export const blankFields = () => ({
  date: todayInput(), space: "Auditorium", material: "", scenes: [blankScene()], breaks: [], house_count: "", audience_reaction: "",
  standing_ovation: "", times: blankTimes(), dept: {}, timer: null,
});

// Saved note -> editable form state (durations as strings for the inputs).
export const fieldsFromNote = (n) => {
  const f = n.fields || {};
  const times = blankTimes();
  Object.keys(times).forEach((k) => {
    times[k] = { time: f.times?.[k]?.time || "", ...(k === "total" ? {} : { duration: f.times?.[k]?.duration ?? "" }) };
  });
  const scenes = f.scenes?.length ? f.scenes.map((s) => ({ key: uid(), scene: s.scene || "", time: s.seconds == null ? "" : fmtClock(s.seconds) })) : [blankScene()];
  return { ...blankFields(), ...f, scenes, breaks: f.breaks || [], dept: { ...(f.dept || {}) }, times };
};

export const toPayload = ({ timer, ...f }) => ({
  ...f,
  scenes: (f.scenes || []).map((s) => ({ scene: s.scene || "", seconds: timer?.key === s.key ? liveSeconds(timer) : parseClock(s.time) })),
  times: Object.fromEntries(Object.entries(f.times).map(([k, r]) => [k, {
    time: r.time || "", duration: k === "total" || r.duration === "" || r.duration == null ? null : Number(r.duration),
  }])),
});

export const totalMinutes = (times) => TIME_ROWS.reduce((s, [k]) => s + (Number(times?.[k]?.duration) || 0), 0);
export const fmtMinutes = (m) => (!m ? "—" : m >= 60 ? `${Math.floor(m / 60)} hr${m % 60 ? ` ${m % 60} min` : ""}` : `${m} min`);
export const noteTitle = (n) => `${KIND_LABEL[n.kind]}${n.report_number ? ` #${n.report_number}` : ""} — ${n.production_name}`;
